import { UserProvider, useVerbiageQuery } from "@repo/baseCloud";
import { address, cityState, MS_IN } from "@repo/helpers";
import { MvcLocation } from "@repo/models";
import { Loading, PortalLayout, TLPComponents, TLPProviders } from "@repo/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { FC, useEffect } from "react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";

import { useLocationQueries } from "./api/locationApi";
import { StatusDot } from "./components/statusDot";
import { LocationProvider, useLocationContext } from "./contexts/editableItemContext";
import { PendingChangesProvider, usePendingChangesContext } from "./contexts/pendingChangesContext";
import { useRouteParams } from "./helpers/urlHooks";
import { layoutStates, usePages } from "./hooks";
import style from "./style.module.scss";
import { Associations, Edit, Summary, StatusHistory } from "./tabs";
import FieldAudits from "./tabs/fieldAudits";

// Module scope: created in the component, every render of App would start a new, empty query cache
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: false,
			staleTime: MS_IN.MINUTE * 15,
		},
	},
});

export const App: FC = () => {
	return (
		<QueryClientProvider client={queryClient}>
			<TLPProviders>
				<BrowserRouter basename="LocationPortal">
					<UserProvider>
						<PendingChangesProvider>
							<LocationProvider>
								<InternalApp />
								<TLPComponents />
							</LocationProvider>
						</PendingChangesProvider>
					</UserProvider>
				</BrowserRouter>
			</TLPProviders>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	);
};

interface AppProps {
	locationId: number | undefined;
	location?: MvcLocation;
}

export const PortalApp: FC<AppProps> = ({ locationId, location }) => {
	return (
		<UserProvider>
			<PendingChangesProvider>
				<LocationProvider item={{ ...location, locationId }}>
					<InternalApp />
					<TLPComponents />
				</LocationProvider>
			</PendingChangesProvider>
		</UserProvider>
	);
};

const InternalApp: FC = () => {
	const { locationId } = useLocationContext();
	const { pendingChanges } = usePendingChangesContext();
	const pages = usePages();

	return (
		<PortalLayout
			title={<Header />}
			headerAction={<HeaderStatus />}
			showSidebar
			defaultActiveState="all"
			defaultPage="summary"
			pages={pages}
			states={layoutStates}
			entityId={locationId?.toString()}
			pendingChanges={pendingChanges}
		>
			<Switch>
				<Route path="/:locationId?/:tab?" component={TabRoute} />
				<Redirect from="*" to="/summary" />
			</Switch>
		</PortalLayout>
	);
};

const TabRoute: FC = () => {
	const { locationId } = useRouteParams();
	const { overwriteItem, locationLoaded, setLocationLoaded } = useLocationContext();
	const { useLocationInfoQuery } = useLocationQueries();
	const locationQuery = useLocationInfoQuery(locationId);
	const { location } = useVerbiageQuery().verbiage;

	useEffect(() => {
		if (!locationId) {
			setLocationLoaded(true);
		} else if (locationQuery.loaded) {
			overwriteItem(locationQuery.data);
			setLocationLoaded(true);
		} else {
			setLocationLoaded(false);
		}
		return () => {
			setLocationLoaded(false);
		};
	}, [locationQuery.data, locationQuery.loaded, overwriteItem, setLocationLoaded, locationId]);

	return (
		<Loading loading={!locationLoaded} message={`Loading ${location}...`} fullHeight>
			<SelectedTab />
		</Loading>
	);
};

const SelectedTab: FC = () => {
	const { tab } = useRouteParams();
	switch (tab) {
		case "associations":
			return <Associations />;
		case "summary":
			return <Summary />;
		case "fieldAudits":
			return <FieldAudits />;
		case "statusHistory":
			return <StatusHistory />;
		default:
			return <Edit />;
	}
};

const Header: FC = () => {
	const { location } = useLocationContext();
	const { location: locationName } = useVerbiageQuery().verbiage;

	if (!location.locationId)
		return (
			<div className={style.headerContainer}>
				<h1 className={style.title}>New {locationName}</h1>
			</div>
		);
	return (
		<div className={style.headerContainer}>
			<h1 className={style.title}>
				{location.name} ({location.locationCode})
			</h1>
			<span className={style.subtitle}>
				{address(location.address1, location.address2)}
				{", "}
				{cityState(location.city, location.stateCode, location.zip)}
			</span>
		</div>
	);
};
const HeaderStatus: FC = () => {
	const { location } = useLocationContext();
	return (
		<span className={style.status}>
			<StatusDot statusId={location.statusId} />
			{location.statusName}
		</span>
	);
};
